<template>
    <div v-floating-tooltip="tippy" :class="requiresMargin ? 'mb-4' : ''">
        <div
            ref="dropZoneRef"
            role="button"
            :tabindex="interactive ? 0 : -1"
            class="border-primary-400/20 bg-primary-800 w-full rounded border border-dashed outline-none transition-colors duration-75"
            :class="[
                interactive ? 'hover:border-primary-400/20 focus-visible:border-accent-400/60 cursor-pointer' : 'cursor-not-allowed opacity-30',
                isOverDropZone ? 'border-accent-400/60 bg-primary-600' : '',
            ]"
            @click="interactive && open()"
            @keydown.enter.prevent="interactive && open()"
            @keydown.space.prevent="interactive && open()"
        >
            <slot :file="file" :is-over-drop-zone="isOverDropZone">
                <div v-if="!file" class="flex flex-col items-center gap-1 p-6 text-center">
                    <fa :icon="['fas', 'cloud-arrow-up']" class="text-primary-400 text-2xl" />

                    <span class="text-primary-300 text-sm">{{ t('generic.file_upload.instructions') }}</span>

                    <span v-if="acceptHint" class="text-primary-400 text-xs">{{ acceptHint }}</span>
                </div>

                <div v-else class="flex items-center gap-3 p-4">
                    <fa :icon="['fas', 'file']" class="text-primary-300" />

                    <div class="min-w-0 flex-1 text-left">
                        <p class="text-primary-50 truncate text-sm font-medium">{{ file.name }}</p>
                        <p class="text-primary-400 text-xs">{{ formatBytes(file.size) }}</p>
                    </div>

                    <v-button
                        type="button"
                        class="text-primary-300 hover:text-primary-50 cursor-pointer"
                        :aria-label="t('generic.file_upload.remove')"
                        @click.stop="clear"
                    >
                        <fa :icon="['fas', 'xmark']" />
                    </v-button>
                </div>
            </slot>
        </div>
    </div>
</template>

<script lang="ts">
import { useDropZone, useFileDialog } from '@vueuse/core';
import { computed, defineComponent, inject, onBeforeMount, onBeforeUnmount, PropType, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { toast } from '~/composables/useToast';
import { translateRequiresPermissions, usePermissionsStore } from '~/plugins/permission';

const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];

export default defineComponent({
    name: 'FormFile',

    props: {
        name: {
            type: String,
            required: true,
        },
        /** Native accept string, e.g. `application/json`, `image/*` or `.map`. Dropped files are validated against it too. */
        accept: {
            type: String,
        },
        /** Comma-separated extension allowlist (e.g. `png,jpg,svg`). Takes precedence over `accept` for validation. */
        extensions: {
            type: String,
        },
        maxSize: {
            type: Number,
        },
        permission: {
            type: [String, Array] as PropType<string | string[]>,
        },
        disabled: {
            type: Boolean,
        },
        noMargin: {
            type: Boolean,
        },
    },

    emits: ['update:file'],

    setup(props, { emit }) {
        const { t } = useI18n();
        const permissions = usePermissionsStore();

        const file = ref<File | undefined>();
        const dropZoneRef = ref<HTMLDivElement>();

        let unregister: any;

        onBeforeMount(() => {
            unregister = inject<registerFormComponentFn | null>(
                'registerFormComponent',
                null,
            )?.({
                key: props.name,
                value: file,
            });
        });

        onBeforeUnmount(() => {
            unregister?.();
            unregister = null;
        });

        const hasPerms = computed(() => (props.permission ? permissions.has(props.permission) : true));
        const interactive = computed(() => hasPerms.value && !props.disabled);

        const formatBytes = (bytes: number): string => {
            if (!bytes) return '0 B';
            const k = 1000;
            const i = Math.min(SIZE_UNITS.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
            return `${(bytes / Math.pow(k, i)).toFixed(1)} ${SIZE_UNITS[i]}`;
        };

        const matchesAccept = (candidate: File): boolean => {
            const ext = candidate.name.split('.').pop()?.toLowerCase() ?? '';

            if (props.extensions) {
                return props.extensions
                    .toLowerCase()
                    .split(',')
                    .map((entry) => entry.trim().replace(/^\./, ''))
                    .includes(ext);
            }

            if (!props.accept) return true;

            return props.accept
                .toLowerCase()
                .split(',')
                .map((entry) => entry.trim())
                .some((pattern) => {
                    if (pattern.startsWith('.')) return `.${ext}` === pattern;
                    if (pattern.endsWith('/*')) return candidate.type.startsWith(pattern.slice(0, -1));
                    return candidate.type === pattern;
                });
        };

        const pick = (candidate: File): void => {
            if (!matchesAccept(candidate)) {
                toast.error(['generic.invalid_file_type']);
                return;
            }

            if (props.maxSize && candidate.size > props.maxSize) {
                toast.error(['generic.file_upload.file_too_large', { max: formatBytes(props.maxSize) }]);
                return;
            }

            file.value = candidate;
            emit('update:file', candidate);
        };

        const clear = (): void => {
            file.value = undefined;
            reset();
            emit('update:file', null);
        };

        const { open, reset, onChange } = useFileDialog({
            accept: props.accept,
            multiple: false,
        });

        onChange((files) => {
            if (files?.[0]) pick(files[0]);
            reset();
        });

        const { isOverDropZone } = useDropZone(dropZoneRef, {
            onDrop: (files) => {
                if (interactive.value && files?.[0]) pick(files[0]);
            },
        });

        return {
            t,
            file,
            dropZoneRef,
            isOverDropZone,
            interactive,
            open,
            clear,
            formatBytes,
            requiresMargin: computed(() => !props.noMargin),
            acceptHint: computed(() => props.extensions?.split(',').join(', ') ?? props.accept),
            tippy: computed(() => {
                if (props.permission && !hasPerms.value) {
                    return [translateRequiresPermissions(props.permission), 'bottom', true];
                }

                return null;
            }),
        };
    },
});
</script>
